import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useView } from '../../utils/useView';
import { Link, useNavigate } from 'react-router-dom';
import useAuthUtils from '../../utils/useAuthUtils';
import useMessages from '../../utils/useMessages';
import { selectSliceSession } from '../../store/reducers/userReducer';

import FormGroup from '../../components/controls/Form/components/FormGroup';
import InputField from '../../components/controls/Form/components/InputField';
import From from '../../components/controls/Form';
import Button from '../../components/controls/Button';
import { apiGET } from '../../utils/networking';
import TextBody from '../../components/controls/Typography/TextBody';
import Heading from '../../components/controls/Typography/Heading';

import './Login.less';

const Login = () => {
  const { isViewDeprecated } = useView();
  const auth = useAuthUtils();
  const navigate = useNavigate();
  const messages = useMessages();
  const session = useSelector(selectSliceSession);
  const [isBusy, setIsBusy] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [needHelpLink, setNeedHelpLink] = useState('');

  const suspnedControls = () => {
    return isViewDeprecated() || auth.isAuthorizing();
  }

  const suspendLoginButton = () => {
    return !username || !password || isViewDeprecated() || auth.isAuthorizing();
  };

  /**
   *
   * @param {import('react').FormEvent<HTMLFormElement>} e
   * @returns {Promise<void>}
   */
  const handleFormSubmit = async e => {
    e.preventDefault();
    setIsBusy(true);
    try {
      await auth.authAsAccount(username, password);
      auth.flows.reUseEvalGuest.isActive() && auth.flows.reUseEvalGuest.data?.reuseForFutureEvals
        ? messages.notifyGreen(
          `Reuse is enabled for ${auth.flows.reUseEvalGuest.data?.applicantFirstName}  ${auth.flows.reUseEvalGuest.data?.applicantLastName}. View your submitted recommendation or stop reusing the recommendation below.`
        )
        : void 0;
    } catch (errorCode) {
      console.error(`Login failed with error code ${errorCode}`);
      errorCode === 1 ? navigate('/accountLocked', { replace: true }) : void 0;
      return;
    } finally {
      setIsBusy(false);
    }
    await verifyGDPR();
  };

  const verifyGDPR = async () => {
    try {
      setIsBusy(true);
      const resp = await apiGET('/recommendation/rest/gdpr');
      resp.status === 200 ? navigate('/dashboard') : navigate('/gdpr');
    } catch (err) {
      console.error(err);
      navigate('/gdpr');
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    document.title = 'Letters Portal - Login';
    const branding = session.branding;
    const footerLinks = [
      ...((branding && branding.footerDTO && branding.footerDTO.footerLinks) || []),
      {
        text: 'Frequently Asked Questions',
        link: 'https://help.liaisonedu.com/Letters_by_Liaison_Help_Center',
      },
    ];
    const needHelpLink = footerLinks.find(v => v.text === 'Frequently Asked Questions');

    // custom logic, since we wipe out session forcefully
    // we must back-up guest+flow data before clenup and restore them
    auth.doLogout().finally(() => {
      setNeedHelpLink(needHelpLink?.link || '');
      setUsername(auth.flows.reUseEvalGuest.data?.evalEmailAddress ?? '');
    });
    return () => {
      auth.continueLoggedOut();
    };
  }, []);

  const render = () => {
    const branding = session.branding;
    const brandingData = branding && branding.footerDTO;
    const errorTag = auth.lastErrorMessage ? (
      <p className="text-center" role={'alert'} tabIndex={0} dangerouslySetInnerHTML={{ __html: auth.lastErrorMessage }} />
    ) : (
      ''
    );
    const helpLink =
      auth.lastErrorMessage && !brandingData ? (
        <p role={'alert'} tabIndex={0} className={'text-center'}>
          <a
            className="lsn-password-link"
            href="https://help.liaisonedu.com/Letters_by_Liaison_Help_Center/Accessing_Your_Account/2_Access_an_Existing_Account"
            target="_blank"
            rel="noreferrer noopener"
          >
            Forgot your username or password?
          </a>
        </p>
      ) : (
        ''
      );
    return (
      <main className="lsn-page lsn-page-login container" aria-live="polite">
        {auth.loggedOutByIdle() ? (
          <div
            className="lsn-timeout-banner-message"
            aria-label={`Your session has timed out due to inactivity, Please log back in to continue working.`}
          >
            <span className="lsn-timeout-banner-text no-focus-outline" tabIndex={0}>
              Your session has timed out due to inactivity, Please log back in to continue working.
            </span>
          </div>
        ) : null}

        <Heading className='lsn-login-header no-focus-outline text-center' tabIndex={0} aria-label={`Welcome to Letters portal by Liaison`}>
          Welcome
        </Heading>

        <From
          isNarrow
          hasBorder
          title='Log in to view a list of all your recommendation requests:'
          isButtonsStack
          onSubmit={handleFormSubmit}
          noAutocomplete
          errors={errorTag || helpLink ? [errorTag, helpLink] : null}
          formAttr={{
            title: 'Login'
          }}
          buttons={<>
            <Button
              type="submit"
              isBusy={isBusy}
              aria-label={suspendLoginButton() ? 'Log In button disabled' : 'Log In button clickable'}
              disabled={suspendLoginButton()}
              primary>{auth.guest?.reuseForFutureEvals ||
                auth.guest?.eligibleForAutoFill ||
                auth.flows.reUseEvalGuest.isActive()
                ? 'Log In / Reuse Recommendation'
                : 'Log In'}</Button>
            <div className="forgot-password-links w-full" hidden={isViewDeprecated()}>
              <Link
                to="/forgotPassword"
                tabIndex={0}
                role="link"
                rel="noreferrer noopener"
                onClick={(e) => { suspnedControls() && e.preventDefault(); }}
              >
                Forgot your Password?
              </Link>
              <a
                href={needHelpLink}
                target="_blank"
                rel="noreferrer noopener"
              >
                Need Help?
              </a>
            </div>
          </>}
          extraFooter={<>
            <TextBody paragraph bold className='text-center'>OR</TextBody>

            <TextBody bold>Create an account:</TextBody>

            <TextBody paragraph>If this is your first time using this service, please create an account to access your recommendation requests</TextBody>

            <Button
              type="button"
              onClick={() => {
                navigate(isViewDeprecated() ? '/login' : '/register');
              }}
              primary
              outline
              className="create-account"
              disabled={isBusy}
            >
              Create Account
            </Button>
          </>}
        >

          <FormGroup
            label='Email/Username'
            required
            horizontal
            labelSize={4}
            controlRender={({ id }) => <InputField
              inputType={'username'}
              title={'Email/Username'}
              name={'username'}
              id={id}
              disabled={suspnedControls()}
              onChange={(n, value) => setUsername(value)}
              value={username}
              placeholder={'Email/Username'}
              required={true}
            />}
          />

          <FormGroup
            label='Password'
            required
            horizontal
            labelSize={4}
            controlRender={({ id }) =>
              <InputField
                inputType={'password'}
                title={'Password'}
                name={'password'}
                id={id}
                disabled={suspnedControls()}
                onChange={(n, value) => setPassword(value)}
                value={password}
                placeholder={'********'}
                required={true}
              />}
          />
        </From>
      </main>
    );
  };

  return render();
};

export default Login;
